<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import { useLlmCredentialStore } from 'dashboard/store/captain/llmCredentials';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  featureKey: { type: String, required: true },
});

const emit = defineEmits(['change']);

const { t } = useI18n();
const { features } = storeToRefs(useCaptainConfigStore());
const { records } = storeToRefs(useLlmCredentialStore());

const feature = computed(() => features.value[props.featureKey]);

// Only the LLMs of the model's provider: one of another provider would be refused by it, and the
// server ignores that choice anyway (Llm::CredentialResolver).
const providerCredentials = computed(() =>
  records.value.filter(record => record.provider === feature.value?.provider)
);

// Empty is the normal case: the provider's first LLM, which the option names.
const options = computed(() => {
  const fallback = providerCredentials.value[0];
  return [
    {
      value: '',
      label: fallback
        ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.AUTOMATIC_WITH', {
            name: fallback.name,
          })
        : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.AUTOMATIC'),
    },
    ...providerCredentials.value.map(record => ({
      value: record.id,
      label: record.name,
    })),
  ];
});

const selected = computed({
  get: () =>
    providerCredentials.value.find(
      record => String(record.id) === String(feature.value?.credential_id)
    )?.id || '',
  set: value =>
    emit('change', { feature: props.featureKey, credential: value }),
});
</script>

<template>
  <div class="w-60">
    <ComboBox
      v-model="selected"
      :options="options"
      :placeholder="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.AUTOMATIC')"
    />
  </div>
</template>
